import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Badge,
  Alert,
  Modal,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaArrowLeft,
  FaSearch,
  FaCog,
} from "react-icons/fa";
import { useParametrosGenerales } from "../../../services/hooks/useParametrosGenerales";

const CLAVES_LOGIFLEX = [
  "envio_consumo_logiflex_ingreso_manual",
  "envio_consumo_logiflex_carga_masiva",
];

const truncar = (texto, max = 80) => {
  if (!texto) return "—";
  const s = String(texto);
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const MantenimientoParametrosGenerales = () => {
  const navigate = useNavigate();
  const {
    parametros,
    loading,
    fetchParametros,
    createParametro,
    updateParametro,
    deleteParametro,
  } = useParametrosGenerales();

  const [filtro, setFiltro] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [parametroEdit, setParametroEdit] = useState(null);
  const [formData, setFormData] = useState({
    clave: "",
    valor: "",
    descripcion: "",
  });

  useEffect(() => {
    fetchParametros();
  }, [fetchParametros]);

  const parametrosFiltrados = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return parametros;
    return parametros.filter(
      (p) =>
        p.clave?.toLowerCase().includes(q) ||
        p.valor?.toLowerCase().includes(q) ||
        (p.descripcion || "").toLowerCase().includes(q)
    );
  }, [parametros, filtro]);

  const handleCloseModal = () => {
    setShowModal(false);
    setParametroEdit(null);
    setFormData({ clave: "", valor: "", descripcion: "" });
  };

  const handleOpenCreate = () => {
    setModalMode("create");
    setFormData({ clave: "", valor: "0", descripcion: "" });
    setParametroEdit(null);
    setShowModal(true);
  };

  const handleOpenEdit = (param) => {
    setModalMode("edit");
    setParametroEdit(param);
    setFormData({
      clave: param.clave || "",
      valor: param.valor ?? "",
      descripcion: param.descripcion || "",
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      clave: formData.clave.trim(),
      valor: formData.valor,
      descripcion: formData.descripcion.trim() || null,
    };
    if (modalMode === "create") {
      await createParametro(payload);
    } else {
      await updateParametro(parametroEdit.id, payload);
    }
    handleCloseModal();
    fetchParametros(filtro);
  };

  const handleDelete = async (param) => {
    if (
      !window.confirm(
        `¿Eliminar el parámetro "${param.clave}"?\nEsta acción no se puede deshacer.`
      )
    ) {
      return;
    }
    await deleteParametro(param.id);
  };

  const aplicarPlantillaLogiflex = (tipo) => {
    if (tipo === "ingreso") {
      setFormData({
        clave: "envio_consumo_logiflex_ingreso_manual",
        valor: "[1]",
        descripcion:
          "Encolar envío a Logiflex al registrar ingreso manual. 0=off, 1=todos, JSON [id_proyecto,...]",
      });
    } else {
      setFormData({
        clave: "envio_consumo_logiflex_carga_masiva",
        valor: "[1]",
        descripcion:
          "Encolar envío a Logiflex tras carga masiva Excel. 0=off, 1=todos, JSON [id_proyecto,...]",
      });
    }
  };

  return (
    <>
      <style>
        {`
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 20px 20px;
            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
          }
          .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
          .dh-main-subtitle { opacity: 0.9; margin: 0.5rem 0 0; }
          .dh-card {
            border: none;
            border-radius: 15px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.08);
            overflow: hidden;
          }
          .dh-card-header {
            padding: 1rem 1.5rem;
            font-weight: 600;
            color: white;
          }
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            font-weight: 600;
            border-radius: 8px;
          }
          .btn-dh-primary:hover { opacity: 0.92; color: white; }
          .btn-dh-secondary {
            background: #6c757d;
            border: none;
            color: white;
            border-radius: 8px;
          }
          .dh-form-label { font-weight: 600; color: #495057; }
          .dh-form-control {
            border-radius: 8px;
            border: 2px solid #e9ecef;
          }
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
          .dh-alert-info {
            background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
            border: none;
            border-radius: 10px;
            color: #1565c0;
          }
          .clave-logiflex { font-family: monospace; font-size: 0.85rem; }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <div className="dh-main-header">
          <Container>
            <Button
              variant="link"
              className="text-white p-0 mb-2"
              onClick={() => navigate(-1)}
            >
              <FaArrowLeft className="me-2" />
              Volver
            </Button>
            <h1 className="dh-main-title">
              <FaCog className="me-2" />
              Parámetros generales
            </h1>
            <p className="dh-main-subtitle">
              Configuración clave-valor del sistema (incluye flags Logiflex / cola Redis)
            </p>
          </Container>
        </div>

        <Container className="pb-5">
          <Alert className="dh-alert dh-alert-info mb-4">
            <strong>Logiflex — envío automático:</strong> use{" "}
            <code>envio_consumo_logiflex_ingreso_manual</code> para ingreso manual y{" "}
            <code>envio_consumo_logiflex_carga_masiva</code> para Excel. Valores:{" "}
            <code>0</code> desactivado, <code>1</code> todos los proyectos,{" "}
            <code>[3,5]</code> solo esos id_proyecto.
          </Alert>

          <Card className="dh-card mb-4">
            <div className="dh-card-header dh-card-header-primary d-flex justify-content-between align-items-center">
              <span>Listado</span>
              <Button className="btn-dh-primary btn-sm" onClick={handleOpenCreate}>
                <FaPlus className="me-1" /> Nuevo parámetro
              </Button>
            </div>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <div className="position-relative">
                    <FaSearch
                      className="position-absolute text-muted"
                      style={{ left: 12, top: 12 }}
                    />
                    <Form.Control
                      className="dh-form-control ps-5"
                      placeholder="Buscar por clave, valor o descripción..."
                      value={filtro}
                      onChange={(e) => setFiltro(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") fetchParametros(filtro);
                      }}
                    />
                  </div>
                </Col>
                <Col md="auto">
                  <Button
                    variant="outline-secondary"
                    onClick={() => fetchParametros(filtro)}
                    disabled={loading}
                  >
                    Actualizar
                  </Button>
                </Col>
              </Row>

              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Clave</th>
                      <th>Valor</th>
                      <th>Descripción</th>
                      <th style={{ width: 120 }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && !parametrosFiltrados.length ? (
                      <tr>
                        <td colSpan={4} className="text-center py-4 text-muted">
                          Cargando...
                        </td>
                      </tr>
                    ) : null}
                    {!loading && !parametrosFiltrados.length ? (
                      <tr>
                        <td colSpan={4} className="text-center py-4 text-muted">
                          No hay parámetros registrados
                        </td>
                      </tr>
                    ) : null}
                    {parametrosFiltrados.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <span
                            className={
                              CLAVES_LOGIFLEX.includes(p.clave)
                                ? "clave-logiflex"
                                : ""
                            }
                          >
                            {p.clave}
                          </span>
                          {CLAVES_LOGIFLEX.includes(p.clave) ? (
                            <Badge bg="info" className="ms-2">
                              Logiflex
                            </Badge>
                          ) : null}
                        </td>
                        <td title={p.valor}>{truncar(p.valor, 60)}</td>
                        <td>{truncar(p.descripcion, 50)}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleOpenEdit(p)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(p)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === "create" ? "Nuevo parámetro" : "Editar parámetro"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            {modalMode === "create" ? (
              <div className="mb-3 d-flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline-info"
                  onClick={() => aplicarPlantillaLogiflex("ingreso")}
                >
                  Plantilla ingreso Logiflex
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline-info"
                  onClick={() => aplicarPlantillaLogiflex("excel")}
                >
                  Plantilla carga masiva Logiflex
                </Button>
              </div>
            ) : null}
            <Form.Group className="mb-3">
              <Form.Label className="dh-form-label">Clave *</Form.Label>
              <Form.Control
                className="dh-form-control"
                required
                maxLength={120}
                value={formData.clave}
                onChange={(e) =>
                  setFormData({ ...formData, clave: e.target.value })
                }
                placeholder="ej. envio_consumo_logiflex_ingreso_manual"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="dh-form-label">Valor *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                className="dh-form-control"
                required
                value={formData.valor}
                onChange={(e) =>
                  setFormData({ ...formData, valor: e.target.value })
                }
                placeholder="0 | 1 | [3,5,7]"
              />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label className="dh-form-label">Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                className="dh-form-control"
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button className="btn-dh-secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button type="submit" className="btn-dh-primary" disabled={loading}>
              Guardar
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default MantenimientoParametrosGenerales;
